/**
 * Validation, enumeration, and fire-time preview for advanced recurring cron schedules.
 */
import type { FrequencyUnit } from '@/utils/cron/types';

import { convertLocalToUtcHours, mergeCrossingMidnightHourField } from './cronUtils';
import type { RecurringCronFormState } from './recurringCronSchedule';
import {
  convertLocalTimeToUTC,
  convertUTCToLocalTime,
  formatGmtPrefix,
  getBrowserTimezone,
} from './timezoneData';

export interface UtcTimeSlot {
  hour: number;
  minute: number;
}

export interface FireInstant {
  utc: Date;
  utcHour: number;
  utcMinute: number;
  localHour: number;
  localMinute: number;
}

export interface RecurringValidationResult {
  valid: boolean;
  errors: string[];
}

const HOUR_RANGE_STEP_PATTERN = /^(\d+)-(\d+)(?:\/(\d+))?$/;

/** Local wall-clock window crosses midnight when the ending hour is earlier on the clock. */
export function isCrossMidnightLocalTimeFrame(hoursFrom: number, hoursTo: number): boolean {
  return hoursFrom > hoursTo;
}

/** Same wall-clock hour for start and end — zero-length window, not allowed. */
export function isSameLocalTimeFrameHour(hoursFrom: number, hoursTo: number): boolean {
  return hoursFrom === hoursTo;
}

/**
 * Inclusive local wall-clock span in whole hours (1–23).
 * Cross-midnight: 4 AM → 12 AM (next day) = 20 hrs. Same-day: 9 AM → 5 PM = 8 hrs.
 */
export function computeLocalTimeFrameDurationHours(hoursFrom: number, hoursTo: number): number {
  if (isSameLocalTimeFrameHour(hoursFrom, hoursTo)) {
    return 0;
  }
  if (isCrossMidnightLocalTimeFrame(hoursFrom, hoursTo)) {
    return 24 - hoursFrom + hoursTo;
  }
  return hoursTo - hoursFrom;
}

/** Local wall-clock hours from start through end inclusive, stepping by stepHours. */
export function enumerateLocalFireHours(
  startHour: number,
  endHour: number,
  stepHours: number
): number[] {
  if (stepHours < 1) return [];
  const hours: number[] = [];
  for (let h = startHour; h <= endHour; h += stepHours) {
    hours.push(h);
  }
  return hours;
}

function fireHoursMatch(
  startHour: number,
  endHour: number,
  stepHours: number,
  fireHours: number[]
): boolean {
  const rebuilt = enumerateLocalFireHours(startHour, endHour, stepHours);
  return rebuilt.length === fireHours.length && rebuilt.every((h, i) => h === fireHours[i]);
}

/**
 * Infer inclusive time-frame end hour from parsed fire hours.
 * Comma-list UTC crons only encode fire slots — the end hour is ambiguous.
 * Return the widest end hour that preserves the same fire list (e.g. 3,12,21 / 9h
 * → 23 not 21 or 22).
 */
export function inferTimeFrameEndHour(
  startHour: number,
  fireHours: number[],
  stepHours: number,
  _viewerTimeZone?: string
): number {
  if (fireHours.length === 0) return startHour;
  const lastFire = fireHours[fireHours.length - 1];
  if (stepHours < 1) return lastFire;

  let maxEnd = lastFire;
  for (let end = lastFire; end <= 23; end++) {
    if (fireHoursMatch(startHour, end, stepHours, fireHours)) {
      maxEnd = end;
    } else {
      break;
    }
  }

  // Comma-hour lists drop the inclusive window cap. When the next step after the
  // last fire would fall past end-of-day, recover the widest end (e.g. 21 → 23).
  // Otherwise the last fire is already the natural cap (e.g. every 2h ending 5PM).
  const nextFireAfterLast = lastFire + stepHours;
  if (nextFireAfterLast > 23) {
    return maxEnd;
  }

  return lastFire;
}

/** Hour-range/step field for UTC wall-clock recurring hours + time frame (legacy/production shape). */
export function buildUtcHoursTimeFrameHourField(form: RecurringCronFormState): string {
  return `${form.hoursFrom}-${form.hoursTo}/${form.frequency}`;
}

/** Local wall-clock minute offsets from startHour:00 through endHour:59 inclusive. */
export function enumerateLocalFireMinutes(
  startHour: number,
  endHour: number,
  stepMinutes: number
): Array<{ hour: number; minute: number }> {
  if (stepMinutes < 1) return [];
  const startTotal = startHour * 60;
  const endTotal = endHour * 60 + 59;
  const times: Array<{ hour: number; minute: number }> = [];
  for (let t = startTotal; t <= endTotal; t += stepMinutes) {
    times.push({ hour: Math.floor(t / 60), minute: t % 60 });
  }
  return times;
}

/** Local wall-clock minutes when the window crosses midnight (end hour is earlier on the clock). */
function enumerateLocalFireMinutesCrossMidnight(
  startHour: number,
  endHour: number,
  stepMinutes: number
): Array<{ hour: number; minute: number }> {
  if (stepMinutes < 1) return [];
  const startTotal = startHour * 60;
  const endTotal = endHour * 60 + 59 + 24 * 60;
  const times: Array<{ hour: number; minute: number }> = [];
  for (let t = startTotal; t <= endTotal; t += stepMinutes) {
    times.push({ hour: Math.floor(t / 60) % 24, minute: t % 60 });
  }
  return times;
}

function uniqueSortedHours(hours: number[]): number[] {
  return [...new Set(hours)].sort((a, b) => a - b);
}

function isContiguousHours(hours: number[]): boolean {
  if (hours.length <= 1) return true;
  for (let i = 1; i < hours.length; i++) {
    if (hours[i] - hours[i - 1] !== 1) return false;
  }
  return true;
}

function splitContiguousHourSegments(hours: number[]): number[][] {
  if (hours.length === 0) return [];
  const segments: number[][] = [[hours[0]]];
  for (let i = 1; i < hours.length; i++) {
    const prev = hours[i - 1];
    const curr = hours[i];
    if (curr - prev === 1) {
      segments[segments.length - 1].push(curr);
    } else {
      segments.push([curr]);
    }
  }
  return segments;
}

function formatHourSegment(segment: number[]): string {
  if (segment.length === 1) {
    return String(segment[0]);
  }
  return `${segment[0]}-${segment[segment.length - 1]}`;
}

/**
 * Compress sorted UTC hours into an EventBridge hour field (for minutes-mode builds).
 * Prefers midnight-cross form `12-23,0-0` when applicable.
 */
export function utcHoursToCronHourField(hours: number[]): string {
  const unique = uniqueSortedHours(hours);
  if (unique.length === 0) {
    throw new Error('No UTC hours to encode');
  }
  if (unique.length === 1) {
    return String(unique[0]);
  }

  if (isContiguousHours(unique)) {
    return `${unique[0]}-${unique[unique.length - 1]}`;
  }

  const segments = splitContiguousHourSegments(unique);

  if (segments.length === 2) {
    const [first, second] = segments;
    const morning = first[0] <= second[0] ? first : second;
    const evening = first[0] <= second[0] ? second : first;
    if (morning[0] === 0 && evening[evening.length - 1] === 23) {
      return `${formatHourSegment(evening)},${formatHourSegment(morning)}`;
    }
  }

  return segments.map(formatHourSegment).join(',');
}

/** Hours + time frame path → cron minute and hour field strings. */
export function utcSlotsToCronFields(slots: UtcTimeSlot[]): { minute: string; hour: string } {
  if (slots.length === 0) {
    throw new Error('No fire times in the selected window');
  }

  const minutes = slots.map((s) => s.minute);
  const firstMinute = minutes[0];
  if (!minutes.every((m) => m === firstMinute)) {
    throw new Error('Fire times must share the same UTC minute offset');
  }

  const hours = uniqueSortedHours(slots.map((s) => s.hour));
  return {
    minute: String(firstMinute),
    hour: hours.join(','),
  };
}

function slotsAlignWithMinuteStepCadence(slots: UtcTimeSlot[], stepMinutes: number): boolean {
  if (slots.length === 0) return false;
  const minutes = slots.map((s) => s.minute);
  return minutes.every((m) => m % stepMinutes === minutes[0] % stepMinutes);
}

function utcSlotKey(slot: UtcTimeSlot): string {
  return `${slot.hour}:${slot.minute}`;
}

/** True when EventBridge minute×hour expansion equals the enumerated slot set exactly. */
export function cronFieldsExpandExactlyToSlots(
  minuteField: string,
  hourField: string,
  slots: UtcTimeSlot[]
): boolean {
  const expected = new Set(slots.map(utcSlotKey));
  const expanded = enumerateEventBridgeFires(minuteField, hourField);
  if (expanded.length !== expected.size) {
    return false;
  }
  for (const slot of expanded) {
    if (!expected.has(utcSlotKey(slot))) {
      return false;
    }
  }
  return true;
}

function utcMinutesToCronMinuteField(minutes: number[]): string {
  const unique = [...new Set(minutes)].sort((a, b) => a - b);
  if (unique.length === 1) {
    return String(unique[0]);
  }
  if (
    unique.length > 1 &&
    unique.every((m, i) => i === 0 || m - unique[i - 1] === unique[1] - unique[0])
  ) {
    const step = unique[1] - unique[0];
    if (unique.every((m, i) => m === unique[0] + i * step)) {
      return `${unique[0]}-${unique[unique.length - 1]}/${step}`;
    }
  }
  return unique.join(',');
}

/** When every UTC hour shares the same minute set, encode as comma/range minute × hour field. */
function tryRectangularMinuteHourEncoding(
  slots: UtcTimeSlot[]
): { minute: string; hour: string } | null {
  const minutesByHour = new Map<number, number[]>();
  for (const slot of slots) {
    const list = minutesByHour.get(slot.hour) ?? [];
    list.push(slot.minute);
    minutesByHour.set(slot.hour, list);
  }

  const hours = uniqueSortedHours([...minutesByHour.keys()]);
  if (hours.length === 0) {
    return null;
  }

  const referenceMinutes = uniqueSortedHours(minutesByHour.get(hours[0]) ?? []);
  const referenceKey = referenceMinutes.join(',');
  for (const hour of hours) {
    const minuteKey = uniqueSortedHours(minutesByHour.get(hour) ?? []).join(',');
    if (minuteKey !== referenceKey) {
      return null;
    }
  }

  const minute = utcMinutesToCronMinuteField(referenceMinutes);
  const hour = utcHoursToCronHourField(hours);
  if (!cronFieldsExpandExactlyToSlots(minute, hour, slots)) {
    return null;
  }

  return { minute, hour };
}

/**
 * Legacy dataProtect hour-range conversion: map local wall-clock start/end hours
 * (at :00) to a UTC hour field without enumerating each fire minute.
 */
export function convertLocalHourRangeToUtcHourField(
  hoursFrom: number,
  hoursTo: number,
  timeZone: string,
  refDate: Date = new Date()
): string {
  const utcStart = convertLocalTimeToUTC(hoursFrom, 0, timeZone, refDate);
  const utcEnd = convertLocalTimeToUTC(hoursTo, 0, timeZone, refDate);
  const start = utcStart.hour;
  const end = utcEnd.hour;

  if (start <= end) {
    return `${start}-${end}`;
  }

  return `${start}-23,0-${end}`;
}

/** Minutes + time frame path → cron minute and hour field strings. */
export function utcMinuteSlotsToCronFields(
  slots: UtcTimeSlot[],
  stepMinutes: number,
  legacyUtcHourField?: string,
  allowLegacyHourFieldFallback = true
): { minute: string; hour: string } {
  if (slots.length === 0) {
    throw new Error('No fire times in the selected window');
  }

  const tryCandidate = (minute: string, hour: string): { minute: string; hour: string } | null => {
    if (cronFieldsExpandExactlyToSlots(minute, hour, slots)) {
      return { minute, hour };
    }
    return null;
  };

  if (slotsAlignWithMinuteStepCadence(slots, stepMinutes)) {
    const hours = uniqueSortedHours(slots.map((s) => s.hour));
    const matched = tryCandidate(`*/${stepMinutes}`, utcHoursToCronHourField(hours));
    if (matched) {
      return matched;
    }
  }

  if (allowLegacyHourFieldFallback && legacyUtcHourField) {
    const matched = tryCandidate(`*/${stepMinutes}`, legacyUtcHourField);
    if (matched) {
      return matched;
    }
  }

  const rectangular = tryRectangularMinuteHourEncoding(slots);
  if (rectangular) {
    return rectangular;
  }

  if (!allowLegacyHourFieldFallback) {
    throw new Error(
      'This minute interval cannot be encoded exactly for a fractional-offset timezone. Use hour-based intervals or adjust the time window.'
    );
  }

  if (legacyUtcHourField) {
    return {
      minute: `*/${stepMinutes}`,
      hour: legacyUtcHourField,
    };
  }

  throw new Error(`Fire times must align with */${stepMinutes} minute cadence`);
}

export function isFractionalOffsetTimezone(
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): boolean {
  const gmt = formatGmtPrefix(timeZone, refDate);
  return !gmt.endsWith(':00');
}

function expandHourFieldSegment(segment: string): number[] {
  const stepMatch = segment.match(HOUR_RANGE_STEP_PATTERN);
  if (stepMatch) {
    const start = parseInt(stepMatch[1], 10);
    const end = parseInt(stepMatch[2], 10);
    const step = stepMatch[3] ? parseInt(stepMatch[3], 10) : 1;
    const hours: number[] = [];
    if (start <= end) {
      for (let h = start; h <= end; h += step) {
        hours.push(h);
      }
    } else {
      for (let h = start; h <= 23; h += step) {
        hours.push(h);
      }
      for (let h = 0; h <= end; h += step) {
        hours.push(h);
      }
    }
    return hours;
  }

  const rangeMatch = segment.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    const hours: number[] = [];
    if (start <= end) {
      for (let h = start; h <= end; h++) {
        hours.push(h);
      }
    } else {
      for (let h = start; h <= 23; h++) hours.push(h);
      for (let h = 0; h <= end; h++) hours.push(h);
    }
    return hours;
  }

  if (/^\d+$/.test(segment)) {
    return [parseInt(segment, 10)];
  }

  return [];
}

/** Expand an EventBridge hour field to discrete UTC hours (best-effort). */
export function expandUtcHourField(hourField: string): number[] {
  if (!hourField || hourField === '*' || hourField === '?') {
    return Array.from({ length: 24 }, (_, i) => i);
  }

  const merged = mergeCrossingMidnightHourField(hourField);
  if (merged !== hourField && !merged.includes(',')) {
    return expandHourFieldSegment(merged);
  }

  const hours: number[] = [];
  for (const segment of hourField.split(',')) {
    hours.push(...expandHourFieldSegment(segment.trim()));
  }
  return uniqueSortedHours(hours);
}

function expandMinuteField(minuteField: string): number[] {
  if (minuteField.startsWith('*/')) {
    const step = parseInt(minuteField.slice(2), 10);
    if (isNaN(step) || step < 1) return [0];
    const minutes: number[] = [];
    for (let m = 0; m < 60; m += step) {
      minutes.push(m);
    }
    return minutes;
  }
  const startStepMatch = minuteField.match(/^(\d+)\/(\d+)$/);
  if (startStepMatch) {
    const start = parseInt(startStepMatch[1], 10);
    const step = parseInt(startStepMatch[2], 10);
    if (isNaN(start) || isNaN(step) || step < 1) return [0];
    const minutes: number[] = [];
    for (let m = start; m < 60; m += step) {
      minutes.push(m);
    }
    return minutes;
  }
  if (/^\d+$/.test(minuteField)) {
    return [parseInt(minuteField, 10)];
  }
  const minutes: number[] = [];
  for (const segment of minuteField.split(',')) {
    const trimmed = segment.trim();
    const rangeMatch = trimmed.match(/^(\d+)-(\d+)(?:\/(\d+))?$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      const step = rangeMatch[3] ? parseInt(rangeMatch[3], 10) : 1;
      for (let m = start; m <= end; m += step) {
        minutes.push(m);
      }
      continue;
    }
    if (/^\d+$/.test(trimmed)) {
      minutes.push(parseInt(trimmed, 10));
    }
  }
  return [...new Set(minutes)].sort((a, b) => a - b);
}

/** Expand stored cron minute+hour fields to UTC slots (one day template). */
export function enumerateEventBridgeFires(minuteField: string, hourField: string): UtcTimeSlot[] {
  const hours = expandUtcHourField(hourField);
  const minutes = expandMinuteField(minuteField);
  const slots: UtcTimeSlot[] = [];
  for (const hour of hours) {
    for (const minute of minutes) {
      slots.push({ hour, minute });
    }
  }
  return slots;
}

export function computeFireInstantsFromSlots(
  slots: UtcTimeSlot[],
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): FireInstant[] {
  return slots.map((slot) => {
    const utc = new Date(
      Date.UTC(
        refDate.getUTCFullYear(),
        refDate.getUTCMonth(),
        refDate.getUTCDate(),
        slot.hour,
        slot.minute,
        0
      )
    );
    const local = convertUTCToLocalTime(slot.hour, slot.minute, timeZone, refDate);
    return {
      utc,
      utcHour: slot.hour,
      utcMinute: slot.minute,
      localHour: local.hour,
      localMinute: local.minute,
    };
  });
}

export function computeFireInstantsFromExpression(
  expression: string,
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): FireInstant[] {
  const parts = expression.trim().split(/\s+/);
  if (parts.length < 2) return [];
  const slots = enumerateEventBridgeFires(parts[0], parts[1]);
  return computeFireInstantsFromSlots(slots, timeZone, refDate);
}

export function buildUtcSlotsFromForm(
  form: RecurringCronFormState,
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): UtcTimeSlot[] {
  if (!form.useTimeFrame) {
    if (form.frequencyUnit === 'hours') {
      return [];
    }
    return enumerateEventBridgeFires(`*/${form.frequency}`, '*');
  }

  if (isCrossMidnightLocalTimeFrame(form.hoursFrom, form.hoursTo)) {
    if (form.frequencyUnit === 'hours') {
      const utcHourField = convertLocalToUtcHours(
        `${form.hoursFrom}-${form.hoursTo}/${form.frequency}`,
        timeZone
      );
      const utcStart = convertLocalTimeToUTC(
        form.hoursFrom,
        form.startingMinute,
        timeZone,
        refDate
      );
      return enumerateEventBridgeFires(String(utcStart.minute), utcHourField);
    }

    const localTimes = enumerateLocalFireMinutesCrossMidnight(
      form.hoursFrom,
      form.hoursTo,
      form.frequency
    );
    return localTimes.map(({ hour, minute }) => {
      const utc = convertLocalTimeToUTC(hour, minute, timeZone, refDate);
      return { hour: utc.hour, minute: utc.minute };
    });
  }

  if (form.frequencyUnit === 'minutes') {
    const localTimes = enumerateLocalFireMinutes(form.hoursFrom, form.hoursTo, form.frequency);
    return localTimes.map(({ hour, minute }) => {
      const utc = convertLocalTimeToUTC(hour, minute, timeZone, refDate);
      return { hour: utc.hour, minute: utc.minute };
    });
  }

  const localHours = enumerateLocalFireHours(form.hoursFrom, form.hoursTo, form.frequency);
  return localHours.map((hour) => {
    const utc = convertLocalTimeToUTC(hour, form.startingMinute, timeZone, refDate);
    return { hour: utc.hour, minute: utc.minute };
  });
}

/** Inclusive end of author-local time-frame hour (fires may run through this minute). */
export const TIME_FRAME_END_MINUTE = 59;

function formatWallClockHour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${hour}:00 AM`;
  if (hour === 12) return '12:00 PM';
  return `${hour - 12}:00 PM`;
}

/** 12-hour UTC wall clock with minutes (for grid UTC toggle). */
export function formatUtcWallClockTime(hour: number, minute: number): string {
  const h12 = hour % 12 || 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

/** 12-hour wall clock at the inclusive end of a time-frame hour (e.g. hour 10 → 10:59 AM). */
export function formatTimeFrameEndWallClock(hour: number): string {
  return formatUtcWallClockTime(hour, TIME_FRAME_END_MINUTE);
}

/** 12-hour wall clock at the start boundary of a time-frame window. */
export function formatTimeFrameStartWallClock(hour: number, minute: number): string {
  return formatUtcWallClockTime(hour, minute);
}

function formatTimeFrameHourBucket(hour: number): string {
  if (hour === 0) return '12AM';
  if (hour < 12) return `${hour}AM`;
  if (hour === 12) return '12PM';
  return `${hour - 12}PM`;
}

/** Ending-at dropdown label — hour bucket plus inclusive :59 cap. */
export function formatTimeFrameEndDropdownLabel(hour: number, hoursFrom: number): string {
  const through = formatTimeFrameEndWallClock(hour);
  const nextDay = isCrossMidnightLocalTimeFrame(hoursFrom, hour) ? ' (next day)' : '';
  return `${formatTimeFrameHourBucket(hour)} (through ${through})${nextDay}`;
}

export interface TimeFrameRangeTextOptions {
  startingMinute?: number;
  frequencyUnit?: FrequencyUnit;
}

function resolveTimeFrameStartMinute(
  startingMinute: number,
  frequencyUnit: FrequencyUnit = 'hours'
): number {
  return frequencyUnit === 'minutes' ? 0 : startingMinute;
}

/**
 * Author-local time frame → UTC range text for listing (handles fractional TZ midnight wrap).
 * Example: IST 3:30 AM–11:59 PM → "between 10:00 PM (previous day) and 6:29 PM".
 */
export function formatUtcTimeFrameRangeFromAuthorWindow(
  hoursFrom: number,
  startingMinute: number,
  hoursTo: number,
  timeZone: string,
  refDate: Date = new Date(),
  frequencyUnit: FrequencyUnit = 'hours'
): string {
  const startMinute = resolveTimeFrameStartMinute(startingMinute, frequencyUnit);
  const startUtc = convertLocalTimeToUTC(hoursFrom, startMinute, timeZone, refDate);
  const endUtc = convertLocalTimeToUTC(hoursTo, TIME_FRAME_END_MINUTE, timeZone, refDate);

  const startLabel = formatUtcWallClockTime(startUtc.hour, startUtc.minute);
  let endLabel = formatUtcWallClockTime(endUtc.hour, endUtc.minute);
  if (isCrossMidnightLocalTimeFrame(hoursFrom, hoursTo)) {
    endLabel = `${endLabel} (next day)`;
  }

  const startTotal = startUtc.hour * 60 + startUtc.minute;
  const endTotal = endUtc.hour * 60 + endUtc.minute;
  const startWithDay = startTotal > endTotal ? `${startLabel} (previous day)` : startLabel;

  return `between ${startWithDay} and ${endLabel}`;
}

/**
 * Author-local time frame → viewer wall-clock range text (grid Local toggle, viewer ≠ author).
 * Example: IST 3:30 AM–11:59 PM viewed from Chicago → "between 5:00 PM (previous day) and 1:29 PM".
 */
export function formatViewerLocalTimeFrameRangeFromAuthorWindow(
  hoursFrom: number,
  startingMinute: number,
  hoursTo: number,
  authorTimeZone: string,
  viewerTimeZone: string,
  refDate: Date = new Date(),
  frequencyUnit: FrequencyUnit = 'hours'
): string {
  const startMinute = resolveTimeFrameStartMinute(startingMinute, frequencyUnit);
  const startUtc = convertLocalTimeToUTC(hoursFrom, startMinute, authorTimeZone, refDate);
  const endUtc = convertLocalTimeToUTC(hoursTo, TIME_FRAME_END_MINUTE, authorTimeZone, refDate);

  const startViewer = convertUTCToLocalTime(
    startUtc.hour,
    startUtc.minute,
    viewerTimeZone,
    refDate
  );
  const endViewer = convertUTCToLocalTime(endUtc.hour, endUtc.minute, viewerTimeZone, refDate);

  const startLabel = formatUtcWallClockTime(startViewer.hour, startViewer.minute);
  let endLabel = formatUtcWallClockTime(endViewer.hour, endViewer.minute);
  if (isCrossMidnightLocalTimeFrame(hoursFrom, hoursTo)) {
    endLabel = `${endLabel} (next day)`;
  }

  const startTotal = startViewer.hour * 60 + startViewer.minute;
  const endTotal = endViewer.hour * 60 + endViewer.minute;
  const startWithDay = startTotal > endTotal ? `${startLabel} (previous day)` : startLabel;

  return `between ${startWithDay} and ${endLabel}`;
}

/** Human-readable local time-frame window for tooltips and grid text. */
export function formatLocalTimeFrameRangeText(
  hoursFrom: number,
  hoursTo: number,
  options?: TimeFrameRangeTextOptions
): string {
  const startingMinute = options?.startingMinute ?? 0;
  const startMinute = resolveTimeFrameStartMinute(
    startingMinute,
    options?.frequencyUnit ?? 'hours'
  );
  const startLabel = formatTimeFrameStartWallClock(hoursFrom, startMinute);
  let endLabel = formatTimeFrameEndWallClock(hoursTo);
  if (isCrossMidnightLocalTimeFrame(hoursFrom, hoursTo)) {
    endLabel = `${endLabel} (next day)`;
  }
  return `between ${startLabel} and ${endLabel}`;
}

export function validateRecurringTimeFrame(
  form: RecurringCronFormState,
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): RecurringValidationResult {
  const errors: string[] = [];

  if (!form.useTimeFrame) {
    return { valid: true, errors };
  }

  if (isSameLocalTimeFrameHour(form.hoursFrom, form.hoursTo)) {
    errors.push(
      'Starting and ending hour cannot be the same. Choose a different ending hour (ending at midnight means the next day).'
    );
    return { valid: false, errors };
  }

  try {
    const slots = buildUtcSlotsFromForm(form, timeZone, refDate);
    if (slots.length < 2) {
      if (form.frequencyUnit === 'hours') {
        const secondFireHour = form.hoursFrom + form.frequency;
        if (!isCrossMidnightLocalTimeFrame(form.hoursFrom, form.hoursTo) && secondFireHour <= 23) {
          errors.push(
            `Time frame is too short for every ${form.frequency} hour(s). Ending hour must be at least ${formatWallClockHour(secondFireHour)} to allow two executions.`
          );
        } else {
          errors.push(
            `Time frame is too short for every ${form.frequency} hour(s). Extend the ending hour or reduce the interval to allow at least two executions.`
          );
        }
      } else {
        errors.push(
          `Time frame is too short for every ${form.frequency} minute(s). Extend the ending hour to allow at least two executions.`
        );
      }
    } else if (form.frequencyUnit === 'hours') {
      utcSlotsToCronFields(slots);
    } else {
      const legacyUtcHourField = convertLocalHourRangeToUtcHourField(
        form.hoursFrom,
        form.hoursTo,
        timeZone,
        refDate
      );
      const allowLegacyHourField = !isFractionalOffsetTimezone(timeZone, refDate);
      utcMinuteSlotsToCronFields(slots, form.frequency, legacyUtcHourField, allowLegacyHourField);
    }
  } catch (err) {
    errors.push(err instanceof Error ? err.message : 'Invalid recurring schedule');
  }

  return { valid: errors.length === 0, errors };
}

export function buildCronFieldsFromForm(
  form: RecurringCronFormState,
  timeZone: string = getBrowserTimezone(),
  refDate: Date = new Date()
): { minute: string; hour: string } {
  if (!form.useTimeFrame) {
    if (form.frequencyUnit === 'minutes') {
      return { minute: `*/${form.frequency}`, hour: '*' };
    }
    return { minute: String(form.startingMinute), hour: `*/${form.frequency}` };
  }

  const validation = validateRecurringTimeFrame(form, timeZone, refDate);
  if (!validation.valid) {
    throw new Error(validation.errors[0] ?? 'Invalid recurring schedule');
  }

  const slots = buildUtcSlotsFromForm(form, timeZone, refDate);
  if (form.frequencyUnit === 'minutes') {
    const legacyUtcHourField = convertLocalHourRangeToUtcHourField(
      form.hoursFrom,
      form.hoursTo,
      timeZone,
      refDate
    );
    const allowLegacyHourField = !isFractionalOffsetTimezone(timeZone, refDate);
    return utcMinuteSlotsToCronFields(
      slots,
      form.frequency,
      legacyUtcHourField,
      allowLegacyHourField
    );
  }

  // UTC advanced cron: preserve time-frame end via range/step (e.g. 15 3-23/9),
  // not comma-hour list (15 3,12,21) which loses the ending hour on round-trip.
  if (timeZone === 'UTC') {
    return {
      minute: String(form.startingMinute),
      hour: buildUtcHoursTimeFrameHourField(form),
    };
  }

  return utcSlotsToCronFields(slots);
}

export type { FrequencyUnit };

const NEXT_EXECUTIONS_DEFAULT_COUNT = 10;
const MAX_EXECUTION_SCAN_DAYS = 366 * 2;

function expandNumericCronField(field: string, min: number, max: number): number[] | null {
  if (!field || field === '*' || field === '?') {
    return null;
  }

  const values = new Set<number>();
  for (const segment of field.split(',')) {
    const trimmed = segment.trim();
    const stepMatch = trimmed.match(/^(\d+)-(\d+)(?:\/(\d+))?$/);
    if (stepMatch) {
      const start = parseInt(stepMatch[1], 10);
      const end = parseInt(stepMatch[2], 10);
      const step = stepMatch[3] ? parseInt(stepMatch[3], 10) : 1;
      if (start <= end) {
        for (let value = start; value <= end; value += step) {
          values.add(value);
        }
      } else {
        for (let value = start; value <= max; value += step) {
          values.add(value);
        }
        for (let value = min; value <= end; value += step) {
          values.add(value);
        }
      }
      continue;
    }

    if (/^\*\/(\d+)$/.test(trimmed)) {
      const step = parseInt(trimmed.slice(2), 10);
      for (let value = min; value <= max; value += step) {
        values.add(value);
      }
      continue;
    }

    if (/^\d+$/.test(trimmed)) {
      values.add(parseInt(trimmed, 10));
    }
  }

  return values.size > 0 ? [...values].sort((a, b) => a - b) : [];
}

function matchesNumericCronField(field: string, value: number, min: number, max: number): boolean {
  const allowed = expandNumericCronField(field, min, max);
  return allowed === null || allowed.includes(value);
}

/** DB/EventBridge day-of-week uses 1=Mon .. 7=Sun (legacy builder convention). */
function utcDateToDbDayOfWeek(date: Date): number {
  const jsDow = date.getUTCDay();
  return jsDow === 0 ? 7 : jsDow;
}

function matchesDbDayOfWeekField(field: string, date: Date): boolean {
  return matchesNumericCronField(field, utcDateToDbDayOfWeek(date), 1, 7);
}

function lastDayOfUtcMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/** EventBridge day-of-month: *, ?, numeric, L, nL (legacy builder uses plain L). */
function matchesDayOfMonthField(domField: string, date: Date): boolean {
  if (domField === '*' || domField === '?') {
    return true;
  }

  const lastDayMatch = domField.match(/^(\d*)L$/);
  if (lastDayMatch) {
    const year = date.getUTCFullYear();
    const monthIndex = date.getUTCMonth();
    const lastDay = lastDayOfUtcMonth(year, monthIndex);
    const offset = lastDayMatch[1] ? parseInt(lastDayMatch[1], 10) : 1;
    if (Number.isNaN(offset) || offset < 1) {
      return false;
    }
    return date.getUTCDate() === lastDay - offset + 1;
  }

  return matchesNumericCronField(domField, date.getUTCDate(), 1, 31);
}

function matchesUtcDateFields(
  domField: string,
  monthField: string,
  dowField: string,
  yearField: string,
  date: Date
): boolean {
  return (
    matchesNumericCronField(yearField, date.getUTCFullYear(), 1970, 2099) &&
    matchesNumericCronField(monthField, date.getUTCMonth() + 1, 1, 12) &&
    matchesDayOfMonthField(domField, date) &&
    matchesDbDayOfWeekField(dowField, date)
  );
}

function fireInstantFromUtcDate(date: Date, timeZone: string): FireInstant {
  const local = convertUTCToLocalTime(date.getUTCHours(), date.getUTCMinutes(), timeZone, date);
  return {
    utc: date,
    utcHour: date.getUTCHours(),
    utcMinute: date.getUTCMinutes(),
    localHour: local.hour,
    localMinute: local.minute,
  };
}

/** Format a scheduled UTC instant for preview rows (local + UTC). */
export function formatCronExecutionInstant(instant: FireInstant, timeZone: string): string {
  const formatDateTime = (date: Date, tz: string, suffix: string): string => {
    const formatted = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
    }).format(date);
    return `${formatted} ${suffix}`;
  };

  return `${formatDateTime(instant.utc, timeZone, 'local')} · ${formatDateTime(instant.utc, 'UTC', 'UTC')}`;
}

/**
 * Next N EventBridge cron executions after `after`.
 * Uses custom expansion (not cron-parser) for stored 6-field UTC expressions.
 */
export function computeNextCronExecutions(
  expression: string,
  count: number = NEXT_EXECUTIONS_DEFAULT_COUNT,
  timeZone: string = getBrowserTimezone(),
  after: Date = new Date()
): FireInstant[] {
  const trimmed = expression?.trim();
  if (!trimmed || count < 1) return [];

  const parts = trimmed.split(/\s+/);
  if (parts.length < 5) return [];

  const [minuteField, hourField, domField, monthField, dowField, yearField = '*'] = parts;
  const slots = enumerateEventBridgeFires(minuteField, hourField).sort(
    (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute)
  );
  if (slots.length === 0) return [];

  const executions: FireInstant[] = [];
  const scanStart = Date.UTC(
    after.getUTCFullYear(),
    after.getUTCMonth(),
    after.getUTCDate(),
    0,
    0,
    0,
    0
  );

  for (
    let dayOffset = 0;
    dayOffset < MAX_EXECUTION_SCAN_DAYS && executions.length < count;
    dayOffset++
  ) {
    const dayStart = new Date(scanStart + dayOffset * 86_400_000);
    if (!matchesUtcDateFields(domField, monthField, dowField, yearField, dayStart)) {
      continue;
    }

    for (const slot of slots) {
      const candidate = new Date(
        Date.UTC(
          dayStart.getUTCFullYear(),
          dayStart.getUTCMonth(),
          dayStart.getUTCDate(),
          slot.hour,
          slot.minute,
          0,
          0
        )
      );

      if (candidate.getTime() <= after.getTime()) {
        continue;
      }

      executions.push(fireInstantFromUtcDate(candidate, timeZone));
      if (executions.length >= count) {
        break;
      }
    }
  }

  return executions.sort((a, b) => a.utc.getTime() - b.utc.getTime()).slice(0, count);
}

/**
 * All EventBridge cron UTC fire times in (after, until] for a stored 6-field expression.
 */
export function enumerateCronExecutionsInUtcRange(
  expression: string,
  after: Date,
  until: Date
): Date[] {
  const trimmed = expression?.trim();
  if (!trimmed || after.getTime() >= until.getTime()) return [];

  const parts = trimmed.split(/\s+/);
  if (parts.length < 5) return [];

  const [minuteField, hourField, domField, monthField, dowField, yearField = '*'] = parts;
  const slots = enumerateEventBridgeFires(minuteField, hourField).sort(
    (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute)
  );
  if (slots.length === 0) return [];

  const executions: Date[] = [];
  const scanStart = Date.UTC(
    after.getUTCFullYear(),
    after.getUTCMonth(),
    after.getUTCDate(),
    0,
    0,
    0,
    0
  );

  for (
    let dayOffset = 0;
    dayOffset < MAX_EXECUTION_SCAN_DAYS && executions.length < 10_000;
    dayOffset++
  ) {
    const dayStart = new Date(scanStart + dayOffset * 86_400_000);
    if (dayStart.getTime() > until.getTime()) {
      break;
    }

    if (!matchesUtcDateFields(domField, monthField, dowField, yearField, dayStart)) {
      continue;
    }

    for (const slot of slots) {
      const candidate = new Date(
        Date.UTC(
          dayStart.getUTCFullYear(),
          dayStart.getUTCMonth(),
          dayStart.getUTCDate(),
          slot.hour,
          slot.minute,
          0,
          0
        )
      );

      if (candidate.getTime() <= after.getTime()) {
        continue;
      }
      if (candidate.getTime() > until.getTime()) {
        return executions.sort((a, b) => a.getTime() - b.getTime());
      }

      executions.push(candidate);
    }
  }

  return executions.sort((a, b) => a.getTime() - b.getTime());
}
